import { useEffect, useRef, useState } from 'react';
import { authApi } from '../../api/resources';
import { useSession } from '../../api/session';
import { inpFS } from '../../components/ui/Inputs.jsx';
import { tr } from '../../lib/i18n';
import { C } from '../../theme/palette';
import { useModeConnexion } from '../../api/modeConnexion';
import { oublieOuvrier, ouvrierRetenu, retiensOuvrier } from '../../lib/appareil-ouvrier';

/** Longueur du code d'ouvrier. */
const LONGUEUR_PIN = 4;

/** Longueur du code reçu par e-mail. */
const LONGUEUR_CODE = 6;

/** Chiffres d'un code de secours, écrit « 4821-7730 ». */
const CHIFFRES_SECOURS = 8;

/** « 48217730 » s'écrit « 4821-7730 » pendant la saisie. */
function formateSecours(valeur) {
  const chiffres = String(valeur || '')
    .replace(/\D/g, '')
    .slice(0, CHIFFRES_SECOURS);
  return chiffres.length > 4 ? chiffres.slice(0, 4) + '-' + chiffres.slice(4) : chiffres;
}

/*
 * Présentation de l'écran « Compte » de la maquette V288 : fond bleu marine,
 * carte blanche centrée, onglets sur fond clair, champs et boutons des
 * composants de base.
 */

/** Lien discret sous un formulaire (retour, renvoi du code). */
const lienStyle = {
  display: 'block',
  margin: '12px auto 0',
  background: 'none',
  border: 'none',
  padding: 0,
  color: C.grey,
  fontSize: 10,
  fontWeight: 700,
  cursor: 'pointer',
  fontFamily: 'inherit',
};

/** Champ de saisie : celui de l'écran « Compte » de la maquette (`inpFS`). */
const champStyle = { ...inpFS, outline: 'none' };

/** Bouton principal : le `Btn` plein de la maquette, en bouton d'envoi. */
const boutonStyle = {
  width: '100%',
  background: C.navy,
  color: '#fff',
  border: '1px solid transparent',
  borderRadius: 9,
  padding: '11px 14px',
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: 0.2,
  lineHeight: 1.25,
  textWrap: 'balance',
  overflowWrap: 'break-word',
  cursor: 'pointer',
  fontFamily: 'inherit',
};

/** Le même, contour seulement (`Btn ghost`). */
const boutonSecondaireStyle = {
  ...boutonStyle,
  background: C.white,
  color: C.navy,
  border: '1px solid ' + C.navy,
};

/** Libellé de champ, comme celui de `Inp`. */
function Champ({ id, label, children }) {
  return (
    <div style={{ marginBottom: 10 }}>
      <label
        htmlFor={id}
        style={{
          display: 'block',
          fontSize: 9,
          fontWeight: 700,
          color: C.muted,
          marginBottom: 3,
          textTransform: 'uppercase',
          letterSpacing: 0.4,
        }}
      >
        {label}
      </label>
      {children}
    </div>
  );
}

/** Onglet de la carte, actif ou non. */
function Onglet({ actif, onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        flex: 1,
        textAlign: 'center',
        padding: '9px 4px',
        borderRadius: 8,
        border: 'none',
        cursor: 'pointer',
        fontSize: 11,
        fontWeight: 800,
        fontFamily: 'inherit',
        lineHeight: 1.2,
        overflowWrap: 'break-word',
        minWidth: 0,
        background: actif ? C.navy : 'transparent',
        color: actif ? '#fff' : C.grey,
      }}
    >
      {children}
    </button>
  );
}

/** Titre et consigne d'une étape de la carte. */
function Consigne({ titre, children }) {
  return (
    <>
      {titre && (
        <div style={{ fontSize: 14, fontWeight: 800, color: C.ink, marginBottom: 4 }}>{titre}</div>
      )}
      <div style={{ fontSize: 9.5, color: C.grey, marginBottom: 12, lineHeight: 1.5 }}>
        {children}
      </div>
    </>
  );
}

/**
 * Écran d'entrée : connexion, inscription d'une entreprise et connexion par code
 * d'ouvrier.
 *
 * <p>Les trois chemins aboutissent à une session ouverte par le serveur. Rien
 * n'est décidé ici : l'interface se contente de transmettre ce qui est saisi et
 * d'afficher le refus éventuel, dont le libellé vient du serveur — lui seul sait
 * ce qu'il est prudent de dire.
 *
 * @returns {import('react').ReactElement} l'écran de connexion
 */
/** Jeton de réinitialisation porté par l'adresse, le cas échéant. */
function jetonDeLAdresse() {
  try {
    return new URLSearchParams(window.location.search).get('reinitialisation') || '';
  } catch {
    return '';
  }
}

/** Jeton du lien de connexion porté par l'adresse, le cas échéant. */
function lienDeLAdresse() {
  try {
    return new URLSearchParams(window.location.search).get('lien') || '';
  } catch {
    return '';
  }
}

/** Retire les paramètres de l'adresse, une fois le jeton qu'ils portaient consommé. */
function nettoieLAdresse() {
  try {
    window.history.replaceState({}, '', window.location.pathname);
  } catch {
    // Navigateur sans historique manipulable : sans conséquence.
  }
}

export function ConnexionScreen() {
  const { connexion, connexionCode, connexionSecours, ouvreLien, connexionPin, inscription } =
    useSession();
  // Le serveur dit si l'ouvrier entre encore par « Code ouvrier » : tant que
  // le code à six chiffres n'est pas obligatoire pour tous, rien ne change
  // pour lui.
  const modeServeur = useModeConnexion();
  const codeOuvrier = modeServeur?.codeOuvrier === true;
  // Arriver par le lien reçu par courriel ouvre directement le choix du
  // nouveau mot de passe : l'utilisateur n'a pas à retrouver son chemin.
  const [jeton] = useState(jetonDeLAdresse);
  const [lien] = useState(lienDeLAdresse);
  // L'ouvrier déjà venu sur ce téléphone retrouve son nom : il ne tape que
  // ses quatre chiffres.
  const [retenu, setRetenu] = useState(() => ouvrierRetenu());
  const [mode, setMode] = useState(() => (jetonDeLAdresse() ? 'reinitialisation' : 'connexion'));
  const [erreur, setErreur] = useState('');
  const [message, setMessage] = useState('');
  const [envoi, setEnvoi] = useState(false);

  const [email, setEmail] = useState('');
  const [motDePasse, setMotDePasse] = useState('');
  // Défi rendu par le serveur après le mot de passe : la session attend le
  // code à six chiffres, reçu par e-mail ou par SMS au choix.
  const [defi, setDefi] = useState('');
  const [code, setCode] = useState('');
  const [codeSecours, setCodeSecours] = useState('');
  // Moyens proposés par le serveur et destinations, en partie masquées.
  // « secours » : le compte a encore des codes de secours à proposer.
  const [choix, setChoix] = useState({
    canaux: ['EMAIL'],
    emailMasque: '',
    telephoneMasque: '',
    secours: false,
  });
  // Mode d'où l'on vient avant « Utiliser un code de secours », pour y revenir.
  const [avantSecours, setAvantSecours] = useState('code');
  const [canal, setCanal] = useState('EMAIL');
  const [nomEntreprise, setNomEntreprise] = useState('');
  const [nomComplet, setNomComplet] = useState('');
  const [telephone, setTelephone] = useState('');
  const [adresse, setAdresse] = useState('');
  const [npa, setNpa] = useState('');

  const [codeEntreprise, setCodeEntreprise] = useState(() => retenu?.codeEntreprise || '');
  const [employes, setEmployes] = useState(() =>
    retenu ? [{ id: retenu.employeId, nom: retenu.nom }] : [],
  );
  const [employeId, setEmployeId] = useState(() => retenu?.employeId ?? null);
  const [pin, setPin] = useState('');

  useEffect(() => {
    setErreur('');
    setMessage('');
  }, [mode]);

  // L'ouvrier retenu sur ce téléphone retrouve « Code ouvrier » dès que le
  // serveur dit que ce code vaut. Quand le code à six chiffres vaut pour tous,
  // le téléphone l'oublie : il entre désormais comme chacun.
  const ouvertParDefaut = useRef(false);
  useEffect(() => {
    if (!modeServeur || ouvertParDefaut.current) return;
    ouvertParDefaut.current = true;
    if (!codeOuvrier) {
      oublieOuvrier();
      setRetenu(null);
      if (mode === 'pin') setMode('connexion');
    } else if (retenu && mode === 'connexion') {
      setMode('pin');
    }
    // Une fois, à la lecture du mode : la suite appartient à la personne.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modeServeur]);

  // Ouvert par l'icône installée depuis l'invitation, le téléphone connaît
  // l'entreprise et la fiche, pas encore le nom : on le demande pour saluer
  // l'ouvrier.
  useEffect(() => {
    if (!codeOuvrier || !retenu || retenu.nom) return undefined;
    let annule = false;
    authApi
      .employesAvecPin(retenu.codeEntreprise)
      .then((liste) => {
        const moi = (liste || []).find((e) => e.id === retenu.employeId);
        if (annule || !moi) return;
        const complet = { ...retenu, nom: moi.nom };
        retiensOuvrier(complet);
        setRetenu(complet);
        setEmployes(liste);
      })
      .catch(() => {});
    return () => {
      annule = true;
    };
  }, [retenu, codeOuvrier]);

  /** Exécute une action de connexion en affichant l'échec éventuel. */
  const tente = async (action) => {
    setErreur('');
    setMessage('');
    setEnvoi(true);
    try {
      await action();
    } catch (e) {
      // Le serveur nomme les champs fautifs : c'est cela qu'il faut montrer,
      // non « le corps de la requete contient des champs invalides ».
      const champs = e?.champs && Object.values(e.champs).filter(Boolean);
      setErreur(champs?.length ? champs.join(' · ') : e.message || "La connexion n'a pas abouti.");
    } finally {
      setEnvoi(false);
    }
  };

  // Arriver par un lien de connexion remplace le mot de passe, pas le code :
  // la session attend ensuite le code à six chiffres, comme après un mot de
  // passe juste. Le jeton quitte l'adresse aussitôt : il ne sert qu'une fois,
  // et le laisser rejouerait l'ouverture — pour rien — au prochain
  // rechargement.
  //
  // Le lien ne s'ouvre qu'une fois par montage : en développement, React
  // rejoue les effets, et le second envoi trouvait le lien déjà consommé.
  const ouvertureLancee = useRef(false);
  useEffect(() => {
    if (!lien || ouvertureLancee.current) return;
    ouvertureLancee.current = true;
    nettoieLAdresse();
    tente(async () => {
      const reponse = await ouvreLien(lien);
      if (reponse?.codeRequis) demandeLeCode(reponse);
    });
    // `tente` et `ouvreLien` sont stables pour un jeton donné : l'ouverture ne
    // doit avoir lieu qu'une fois.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lien]);

  /** Le mot de passe est juste : choisir où recevoir le code. */
  const demandeLeCode = (reponse) => {
    setDefi(reponse.defi);
    setCode('');
    setChoix({
      canaux: reponse.canaux?.length ? reponse.canaux : ['EMAIL'],
      emailMasque: reponse.emailMasque || '',
      telephoneMasque: reponse.telephoneMasque || '',
      secours: Boolean(reponse.secours),
    });
    // L'e-mail seul possible : le serveur a déjà envoyé le code, il n'y a
    // rien à choisir.
    if (reponse.envoyePar) {
      setCanal(reponse.envoyePar);
      setMode('code');
    } else {
      setMode('canal');
    }
  };

  /** Envoie le mot de passe ; ouvre le choix du code quand le serveur l'attend. */
  const seConnecte = () =>
    tente(async () => {
      const reponse = await connexion(email, motDePasse);
      if (reponse?.codeRequis) demandeLeCode(reponse);
    });

  /** Fait partir le code par e-mail ou par SMS, puis demande de le saisir. */
  const envoieLeCode = (moyen) =>
    tente(async () => {
      const reponse = await authApi.envoieCode(defi, moyen);
      setDefi(reponse.defi);
      setCanal(moyen);
      setCode('');
      setMode('code');
    });

  const saisieCode = (valeur) => setCode(valeur.replace(/\D/g, '').slice(0, LONGUEUR_CODE));

  const renvoieLeCode = () =>
    tente(async () => {
      const reponse = await authApi.renvoieCode(defi);
      setDefi(reponse.defi);
      setCode('');
      setMessage('Un nouveau code vient d’être envoyé. Seul le dernier reçu est valable.');
    });

  /** « Utiliser un code de secours », quand le compte en a encore. */
  const lienSecours = (depuis) =>
    choix.secours ? (
      <button
        type="button"
        onClick={() => {
          setAvantSecours(depuis);
          setCodeSecours('');
          setErreur('');
          setMessage('');
          setMode('secours');
        }}
        disabled={envoi}
        style={{ ...lienStyle, color: C.teal }}
      >
        {tr('Utiliser un code de secours')}
      </button>
    ) : null;

  const chercheLesEmployes = () =>
    tente(async () => {
      const trouves = await authApi.employesAvecPin(codeEntreprise);
      setEmployes(trouves);
      setEmployeId(null);
      setPin('');
      if (trouves.length === 0) {
        setErreur('Aucun employé de cette entreprise ne dispose d’un code.');
      }
    });

  const saisiePin = (valeur) => {
    const chiffres = valeur.replace(/\D/g, '').slice(0, LONGUEUR_PIN);
    setPin(chiffres);
    if (chiffres.length === LONGUEUR_PIN) {
      const nom = employes.find((e) => e.id === employeId)?.nom || retenu?.nom || '';
      tente(async () => {
        await connexionPin(codeEntreprise, employeId, chiffres);
        retiensOuvrier({ codeEntreprise, employeId, nom });
      }).catch(() => {});
    }
  };

  return (
    <div
      style={{
        height: '100%',
        overflowY: 'auto',
        background: 'linear-gradient(160deg,' + C.navy + ',' + C.navyM + ')',
      }}
    >
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          padding: 20,
          boxSizing: 'border-box',
        }}
      >
        <div style={{ textAlign: 'center', marginBottom: 18 }}>
          <div style={{ fontSize: 26, fontWeight: 900, color: '#fff', letterSpacing: 0.5 }}>
            SwissTech
          </div>
          <div style={{ fontSize: 10, color: 'rgba(255,255,255,.6)', marginTop: 4 }}>
            Gestion BTP & magasin
          </div>
        </div>

        <div
          style={{
            background: '#fff',
            borderRadius: 16,
            padding: 18,
            maxWidth: 380,
            width: '100%',
            margin: '0 auto',
            boxShadow: '0 10px 40px rgba(0,0,0,.25)',
            boxSizing: 'border-box',
          }}
        >
          {['connexion', 'inscription', 'pin'].includes(mode) && (
            <div
              style={{
                display: 'flex',
                gap: 6,
                marginBottom: 14,
                background: C.navyXL,
                borderRadius: 10,
                padding: 4,
              }}
            >
              <Onglet actif={mode === 'connexion'} onClick={() => setMode('connexion')}>
                Connexion
              </Onglet>
              <Onglet actif={mode === 'inscription'} onClick={() => setMode('inscription')}>
                Créer un compte
              </Onglet>
              {codeOuvrier && (
                <Onglet actif={mode === 'pin'} onClick={() => setMode('pin')}>
                  Code ouvrier
                </Onglet>
              )}
            </div>
          )}

          {mode === 'connexion' && (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                seConnecte();
              }}
            >
              <Champ id="email" label="E-mail">
                <input
                  id="email"
                  type="email"
                  autoComplete="username"
                  placeholder="nom@exemple.ch"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  style={champStyle}
                />
              </Champ>
              <Champ id="motDePasse" label="Mot de passe">
                <input
                  id="motDePasse"
                  type="password"
                  autoComplete="current-password"
                  placeholder="••••••"
                  value={motDePasse}
                  onChange={(e) => setMotDePasse(e.target.value)}
                  style={champStyle}
                />
              </Champ>
              <div style={{ marginTop: 6 }}>
                <button type="submit" disabled={envoi} style={boutonStyle}>
                  {envoi ? '…' : 'Se connecter'}
                </button>
              </div>
              <button
                type="button"
                onClick={() => setMode('oubli')}
                style={{ ...lienStyle, color: C.teal }}
              >
                Mot de passe oublié ?
              </button>
              <button
                type="button"
                onClick={() => setMode('lien')}
                style={{ ...boutonSecondaireStyle, marginTop: 12 }}
              >
                Recevoir un lien de connexion par e-mail
              </button>
            </form>
          )}

          {mode === 'canal' && (
            <div>
              <Consigne titre="Recevoir mon code de connexion">
                Pour ta sécurité, SwissTech demande à chaque connexion un code à 6 chiffres. Choisis
                où le recevoir.
              </Consigne>
              {/* Seuls les moyens allumés dans « Ta 2ᵉ sécurité » sont proposés. */}
              {choix.canaux.includes('EMAIL') && (
                <button
                  type="button"
                  onClick={() => envoieLeCode('EMAIL')}
                  disabled={envoi}
                  style={{ ...boutonStyle, marginBottom: 10 }}
                >
                  ✉️ Par e-mail{choix.emailMasque ? ' — ' + choix.emailMasque : ''}
                </button>
              )}
              {choix.canaux.includes('APPLICATION') && (
                <button
                  type="button"
                  onClick={() => envoieLeCode('APPLICATION')}
                  disabled={envoi}
                  style={{ ...boutonSecondaireStyle, marginBottom: 10 }}
                >
                  🔑 Code de mon application Authenticator
                </button>
              )}
              {choix.canaux.includes('SMS') ? (
                <button
                  type="button"
                  onClick={() => envoieLeCode('SMS')}
                  disabled={envoi}
                  style={{ ...boutonSecondaireStyle, marginBottom: 10 }}
                >
                  📱 Par SMS — {choix.telephoneMasque}
                </button>
              ) : (
                <div style={{ fontSize: 9, color: C.grey, lineHeight: 1.5 }}>
                  Pour recevoir le code par SMS, ajoute ton numéro de portable dans Plus → Sécurité
                  une fois connecté.
                </div>
              )}
              {lienSecours('canal')}
              <button
                type="button"
                onClick={() => {
                  setDefi('');
                  setCode('');
                  setMode('connexion');
                }}
                style={{ ...lienStyle, marginTop: 14 }}
              >
                ← Retour
              </button>
            </div>
          )}

          {mode === 'code' && (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                tente(() => connexionCode(defi, code));
              }}
            >
              {canal === 'APPLICATION' ? (
                <Consigne titre="Code de ton application">
                  Mot de passe juste ✓. Ouvre <b>Authenticator</b> et tape le code affiché sous{' '}
                  <b>SwissTech</b>.
                </Consigne>
              ) : canal === 'SMS' ? (
                <Consigne titre="Saisis le code reçu par SMS">
                  Un code à 6 chiffres vient d’être envoyé au <b>{choix.telephoneMasque}</b>. Il est
                  valable 2 minutes.
                </Consigne>
              ) : (
                <Consigne titre="Saisis le code reçu par e-mail">
                  Un code à 6 chiffres vient d’être envoyé à <b>{choix.emailMasque || email}</b>. Il
                  est valable 2 minutes.
                </Consigne>
              )}
              <Champ id="codeConnexion" label="Code de sécurité">
                <input
                  id="codeConnexion"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  placeholder="Code à 6 chiffres"
                  value={code}
                  onChange={(e) => saisieCode(e.target.value)}
                  // Espacé en grand, l'indication « Code à 6 chiffres »
                  // débordait de la case : elle garde l'écriture normale.
                  style={{
                    ...champStyle,
                    textAlign: 'center',
                    ...(code && {
                      letterSpacing: 8,
                      fontSize: 22,
                      fontWeight: 800,
                      fontFamily: 'monospace',
                    }),
                  }}
                />
              </Champ>
              <div style={{ marginTop: 8 }}>
                <button
                  type="submit"
                  disabled={envoi || code.length !== LONGUEUR_CODE}
                  style={boutonStyle}
                >
                  {envoi ? 'Vérification…' : 'Valider le code'}
                </button>
              </div>
              {canal === 'APPLICATION' ? (
                // Application indisponible : le code peut encore venir par
                // e-mail, ou par SMS quand le compte a un portable.
                <>
                  {choix.canaux.includes('EMAIL') && (
                    <button
                      type="button"
                      onClick={() => envoieLeCode('EMAIL')}
                      disabled={envoi}
                      style={{ ...lienStyle, color: C.teal }}
                    >
                      Pas mon téléphone sur moi → recevoir un code par e-mail
                    </button>
                  )}
                  {choix.canaux.includes('SMS') && (
                    <button
                      type="button"
                      onClick={() => envoieLeCode('SMS')}
                      disabled={envoi}
                      style={{ ...lienStyle, color: C.teal }}
                    >
                      Recevoir le code par SMS
                    </button>
                  )}
                </>
              ) : (
                <>
                  <button
                    type="button"
                    onClick={renvoieLeCode}
                    disabled={envoi}
                    style={{ ...lienStyle, color: C.teal }}
                  >
                    Renvoyer le code
                  </button>
                  {choix.canaux.includes('SMS') && (
                    <button
                      type="button"
                      onClick={() => {
                        setCode('');
                        setMode('canal');
                      }}
                      disabled={envoi}
                      style={{ ...lienStyle, color: C.teal }}
                    >
                      {canal === 'SMS' ? 'Recevoir le code par e-mail' : 'Recevoir le code par SMS'}
                    </button>
                  )}
                  {choix.canaux.includes('APPLICATION') && (
                    <button
                      type="button"
                      onClick={() => envoieLeCode('APPLICATION')}
                      disabled={envoi}
                      style={{ ...lienStyle, color: C.teal }}
                    >
                      Utiliser plutôt mon application Authenticator
                    </button>
                  )}
                </>
              )}
              {lienSecours('code')}
              <button
                type="button"
                onClick={() => {
                  setDefi('');
                  setCode('');
                  setMode('connexion');
                }}
                style={{ ...lienStyle, marginTop: 10 }}
              >
                ← Retour
              </button>
            </form>
          )}

          {mode === 'secours' && (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                tente(() => connexionSecours(defi, codeSecours));
              }}
            >
              <Consigne titre={tr('Code de secours')}>
                {tr(
                  'Téléphone perdu ? Tape l’un des codes de secours que tu as imprimés. Chaque code ne sert qu’une fois.',
                )}
              </Consigne>
              <Champ id="codeSecours" label={tr('Code de secours')}>
                <input
                  id="codeSecours"
                  inputMode="numeric"
                  autoComplete="off"
                  placeholder="4821-7730"
                  value={codeSecours}
                  onChange={(e) => setCodeSecours(formateSecours(e.target.value))}
                  style={{
                    ...champStyle,
                    textAlign: 'center',
                    ...(codeSecours && {
                      letterSpacing: 4,
                      fontSize: 20,
                      fontWeight: 800,
                      fontFamily: 'monospace',
                    }),
                  }}
                />
              </Champ>
              <div style={{ marginTop: 8 }}>
                <button
                  type="submit"
                  disabled={envoi || codeSecours.replace(/\D/g, '').length !== CHIFFRES_SECOURS}
                  style={boutonStyle}
                >
                  {envoi ? tr('Vérification…') : tr('Entrer avec ce code de secours')}
                </button>
              </div>
              <button
                type="button"
                onClick={() => {
                  setCodeSecours('');
                  setErreur('');
                  setMode(avantSecours);
                }}
                style={{ ...lienStyle, marginTop: 10 }}
              >
                {tr('← Retour')}
              </button>
            </form>
          )}

          {mode === 'lien' && (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                tente(async () => {
                  await authApi.demandeLien(email);
                  // Le serveur répond de même que l'adresse soit connue ou non :
                  // l'interface ne peut donc rien affirmer de plus.
                  setMessage(
                    'Si un compte correspond à cette adresse, un lien de connexion vient d’y être envoyé. Il est valable 15 minutes et ne sert qu’une fois.',
                  );
                });
              }}
            >
              <Consigne titre="Lien de connexion">
                Pas de mot de passe sous la main ? Indique l’adresse de ton compte : tu recevras un
                lien qui ouvre ta session d’un clic.
              </Consigne>
              <Champ id="emailLien" label="E-mail">
                <input
                  id="emailLien"
                  type="email"
                  autoComplete="username"
                  placeholder="nom@exemple.ch"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  style={champStyle}
                />
              </Champ>
              <div style={{ marginTop: 6 }}>
                <button
                  type="submit"
                  disabled={envoi || !email}
                  style={{ ...boutonStyle, background: C.teal }}
                >
                  {envoi ? 'Envoi…' : 'Recevoir le lien de connexion'}
                </button>
              </div>
              <button type="button" onClick={() => setMode('connexion')} style={lienStyle}>
                ← Revenir à la connexion
              </button>
            </form>
          )}

          {mode === 'oubli' && (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                tente(async () => {
                  await authApi.motDePasseOublie(email);
                  // Le serveur répond de même que l'adresse soit connue ou non :
                  // l'interface ne peut donc rien affirmer de plus.
                  setMessage(
                    'Si un compte correspond à cette adresse, un lien vient d’y être envoyé. Il est valable une heure.',
                  );
                });
              }}
            >
              <Consigne titre="Mot de passe oublié">
                Indique l’adresse de ton compte : tu recevras un lien pour choisir un nouveau mot de
                passe.
              </Consigne>
              <Champ id="emailOubli" label="Ton e-mail">
                <input
                  id="emailOubli"
                  type="email"
                  autoComplete="username"
                  placeholder="nom@exemple.ch"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  style={champStyle}
                />
              </Champ>
              <div style={{ marginTop: 6 }}>
                <button
                  type="submit"
                  disabled={envoi}
                  style={{ ...boutonStyle, background: C.teal }}
                >
                  {envoi ? 'Envoi…' : 'Recevoir le lien'}
                </button>
              </div>
              <button type="button" onClick={() => setMode('connexion')} style={lienStyle}>
                ← Revenir à la connexion
              </button>
            </form>
          )}

          {mode === 'reinitialisation' && (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                tente(async () => {
                  await authApi.reinitialise(jeton, motDePasse);
                  // Le jeton est consommé : le laisser dans l'adresse rouvrirait
                  // ce formulaire au prochain rechargement, pour rien.
                  try {
                    window.history.replaceState({}, '', window.location.pathname);
                  } catch {
                    // Navigateur sans historique manipulable : sans conséquence.
                  }
                  // Un compte protégé par un code (e-mail, SMS, Authenticator)
                  // passe ensuite par l'écran du code, comme à la connexion :
                  // l'écran restait sur le formulaire, sans un mot.
                  const reponse = await connexion(email, motDePasse);
                  if (reponse?.codeRequis) demandeLeCode(reponse);
                });
              }}
            >
              <Consigne titre="Nouveau mot de passe">
                Choisis ton nouveau mot de passe. Les sessions ouvertes ailleurs seront fermées.
              </Consigne>
              <Champ id="emailReinit" label="E-mail">
                <input
                  id="emailReinit"
                  type="email"
                  autoComplete="username"
                  placeholder="nom@exemple.ch"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  style={champStyle}
                />
              </Champ>
              <Champ id="nouveauMotDePasse" label="Nouveau mot de passe">
                <input
                  id="nouveauMotDePasse"
                  type="password"
                  autoComplete="new-password"
                  placeholder="10 caractères au moins"
                  value={motDePasse}
                  onChange={(e) => setMotDePasse(e.target.value)}
                  style={champStyle}
                />
              </Champ>
              <div style={{ marginTop: 6 }}>
                <button type="submit" disabled={envoi} style={boutonStyle}>
                  {envoi ? 'Enregistrement…' : 'Choisir ce mot de passe'}
                </button>
              </div>
            </form>
          )}

          {mode === 'inscription' && (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                tente(async () => {
                  const reponse = await inscription({
                    nomEntreprise,
                    nomComplet,
                    email,
                    motDePasse,
                    telephone,
                    adresse,
                    npa,
                  });
                  // Le compte est créé ; la première connexion passe, elle
                  // aussi, par le code à six chiffres.
                  if (reponse?.codeRequis) demandeLeCode(reponse);
                });
              }}
            >
              <Champ id="nomEntreprise" label="Nom / société">
                <input
                  id="nomEntreprise"
                  placeholder="Exemple Sàrl"
                  value={nomEntreprise}
                  onChange={(e) => setNomEntreprise(e.target.value)}
                  style={champStyle}
                />
              </Champ>
              <Champ id="nomComplet" label="Prénom et nom">
                <input
                  id="nomComplet"
                  autoComplete="name"
                  placeholder="Prénom Nom"
                  value={nomComplet}
                  onChange={(e) => setNomComplet(e.target.value)}
                  style={champStyle}
                />
              </Champ>
              <Champ id="emailInscription" label="E-mail">
                <input
                  id="emailInscription"
                  type="email"
                  autoComplete="username"
                  placeholder="nom@exemple.ch"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  style={champStyle}
                />
              </Champ>
              <Champ id="telephoneInscription" label="Téléphone">
                <input
                  id="telephoneInscription"
                  type="tel"
                  autoComplete="tel"
                  placeholder="079 123 45 67"
                  value={telephone}
                  onChange={(e) => setTelephone(e.target.value)}
                  style={champStyle}
                />
              </Champ>
              <Champ id="adresseInscription" label="Adresse">
                <input
                  id="adresseInscription"
                  autoComplete="street-address"
                  placeholder="Rue et numéro"
                  value={adresse}
                  onChange={(e) => setAdresse(e.target.value)}
                  style={champStyle}
                />
              </Champ>
              <Champ id="npaInscription" label="NPA / Localité">
                <input
                  id="npaInscription"
                  placeholder="1200 Genève"
                  value={npa}
                  onChange={(e) => setNpa(e.target.value)}
                  style={champStyle}
                />
              </Champ>
              <Champ id="motDePasseInscription" label="Mot de passe">
                <input
                  id="motDePasseInscription"
                  type="password"
                  autoComplete="new-password"
                  placeholder="10 caractères au moins"
                  value={motDePasse}
                  onChange={(e) => setMotDePasse(e.target.value)}
                  style={champStyle}
                />
              </Champ>
              <div style={{ marginTop: 6 }}>
                <button type="submit" disabled={envoi} style={boutonStyle}>
                  {envoi ? '…' : 'Créer mon compte'}
                </button>
              </div>
              <div
                style={{
                  fontSize: 9.5,
                  color: C.grey,
                  marginTop: 12,
                  textAlign: 'center',
                  lineHeight: 1.5,
                }}
              >
                Ce compte est celui de la direction : il pourra ensuite ouvrir les accès de
                l’équipe.
              </div>
            </form>
          )}

          {mode === 'pin' && codeOuvrier && (
            <div>
              <Consigne>
                Sur chantier, entre le code d’entreprise affiché par ton responsable, puis ton code
                à quatre chiffres.
              </Consigne>
              <Champ id="codeEntreprise" label="Code d’entreprise">
                <div style={{ display: 'flex', gap: 8 }}>
                  <input
                    id="codeEntreprise"
                    autoCapitalize="characters"
                    autoComplete="off"
                    placeholder="Code d’entreprise"
                    value={codeEntreprise}
                    // Le code se dicte : il arrive en minuscules, parfois avec des
                    // tirets. Le serveur les accepte, la mise en majuscules ici ne
                    // sert qu'à ce que l'ouvrier se relise.
                    onChange={(e) => setCodeEntreprise(e.target.value.toUpperCase())}
                    style={{ ...champStyle, flex: 1, marginBottom: 0 }}
                  />
                  <button
                    type="button"
                    onClick={chercheLesEmployes}
                    disabled={!codeEntreprise || envoi}
                    style={{ ...boutonStyle, width: 'auto' }}
                  >
                    Chercher
                  </button>
                </div>
              </Champ>

              {employes.length > 0 && !employeId && (
                <div style={{ marginTop: 4 }}>
                  {employes.map((employe) => (
                    <button
                      key={employe.id}
                      type="button"
                      onClick={() => {
                        setEmployeId(employe.id);
                        setPin('');
                        setErreur('');
                      }}
                      style={{
                        display: 'flex',
                        width: '100%',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        border: '1px solid ' + C.border,
                        borderRadius: 12,
                        padding: '13px 14px',
                        marginBottom: 10,
                        background: C.white,
                        color: C.ink,
                        fontSize: 12.5,
                        fontWeight: 800,
                        fontFamily: 'inherit',
                        textAlign: 'left',
                        cursor: 'pointer',
                      }}
                    >
                      {employe.nom}
                      <span aria-hidden="true" style={{ color: C.muted, fontSize: 16 }}>
                        ›
                      </span>
                    </button>
                  ))}
                </div>
              )}

              {employeId && (
                <div>
                  {retenu && retenu.employeId === employeId && (
                    <div
                      style={{ fontSize: 14, fontWeight: 800, color: C.ink, margin: '4px 0 10px' }}
                    >
                      👋 Bonjour {retenu.nom || ''}
                    </div>
                  )}
                  <Champ id="pin" label="Code à quatre chiffres">
                    <input
                      id="pin"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      placeholder="••••"
                      value={pin}
                      onChange={(e) => saisiePin(e.target.value)}
                      style={{
                        ...champStyle,
                        letterSpacing: 8,
                        textAlign: 'center',
                        fontSize: 22,
                        fontWeight: 800,
                        fontFamily: 'monospace',
                      }}
                    />
                  </Champ>
                  <button
                    type="button"
                    onClick={() => {
                      // « Ce n'est pas moi » : le téléphone oublie l'ouvrier
                      // retenu, et la liste de l'entreprise revient.
                      if (retenu) {
                        oublieOuvrier();
                        setRetenu(null);
                        setEmployes([]);
                      }
                      setEmployeId(null);
                      setPin('');
                    }}
                    style={lienStyle}
                  >
                    {retenu && retenu.employeId === employeId
                      ? '← Ce n’est pas moi / autre entreprise'
                      : '← Choisir un autre nom'}
                  </button>
                </div>
              )}
            </div>
          )}

          {message && (
            <div
              role="status"
              style={{
                fontSize: 9.5,
                fontWeight: 700,
                color: C.navy,
                marginTop: 12,
                textAlign: 'center',
                lineHeight: 1.5,
              }}
            >
              {message}
            </div>
          )}

          {erreur && (
            <div
              role="alert"
              style={{
                fontSize: 9.5,
                fontWeight: 700,
                color: C.red,
                marginTop: 12,
                textAlign: 'center',
                lineHeight: 1.5,
              }}
            >
              {erreur}
            </div>
          )}
        </div>
        <div
          style={{
            textAlign: 'center',
            fontSize: 8,
            color: 'rgba(255,255,255,.45)',
            marginTop: 16,
          }}
        >
          Tes données sont privées et protégées.
        </div>
      </div>
    </div>
  );
}
